// HUD: the command bar, resource readout, selection panel and command card.
//
//   new HUD({ root, world, player, input })
//     .update()            once per display frame -- DIFFS, never rebuilds
//     .minimapCanvas       the <canvas> the Minimap module draws into
//     .flash(message)      transient centre-screen text
//
// Plain DOM. No framework, no virtual DOM, no innerHTML in the frame loop. The
// element tree is built ONCE in the constructor; update() compares each value
// against what it wrote last time and touches only what changed. At 60 Hz over
// a ten minute match that is the difference between a HUD that costs nothing
// and one that makes every click feel late.
//
// ---------------------------------------------------------------------------
// CONTRACT WITH src/ui/input.js  (the input controller is a separate module)
// ---------------------------------------------------------------------------
// The HUD never invents a command path of its own for anything the player could
// also do with the mouse. It asks the input controller first and falls back
// only so that a missing method degrades into "the button does nothing useful"
// rather than "the game throws".
//
//   SELECTION      hud reads `input.selection` (a Set of entity ids). To change
//                  it, it calls the first of these that exists:
//                    input.setSelection(ids) | input.select(ids)
//                  and otherwise edits input.selection directly and pushes the
//                  result to input.renderer.setSelection().
//
//   ORDERS         untargeted commands (Stop, Hold, Train, Research, Siege,
//                  Cloak, Cancel) are issued by the HUD itself through
//                  world.issueNow() -- the same zero-latency path the mouse
//                  uses. Nothing is gained by bouncing them off the input layer.
//
//   TARGETED       Move / Attack / Patrol / Gather / Repair / Set Rally / Build
//                  need a follow-up click, which is the input layer's job. The
//                  HUD calls the first of:
//                    build:  input.beginPlacement(defId, ids)
//                    others: input.beginTargeting({type, ids, player, defId})
//                  falling back through startTargeting / setPending /
//                  beginCommand. It never synthesises a keypress to get there:
//                  input.js resolves keys off this same card, so a synthetic
//                  key would be a second route to one behaviour.
//
//   HOTKEYS        src/ui/input.js OWNS THE KEYBOARD, and not by agreement.
//                  It resolves every key against commandCard() -- this file's
//                  own table, rebuilt fresh on each press -- so there is no
//                  button on this card it cannot reach, and therefore no reason
//                  for a second listener. `hud.ownsHotkeys` is a GETTER that
//                  reports false whenever an input controller is attached, so
//                  the HUD's keydown listener cannot fire even if something
//                  assigns `hud.ownsHotkeys = true`. It runs only in the
//                  degenerate case of a HUD with no input layer at all.
//                  `actionForKey(key)` / `runAction(btn, source)` remain the
//                  button-execution path used by mouse clicks.
//
//   EVENTS         src/render/scene.js drains world.drainEvents() once per frame
//                  and republishes the batch via renderer.getFrameEvents(). The
//                  HUD reads THAT, and only falls back to draining the world
//                  itself when it is running without a renderer (unit tests).
//                  Exactly one module drains; see contractNotes.
//
//   GEOMETRY       `hud.isOverHud(x, y)` is true for screen points covered by
//                  the command bar, and `hud.barHeight` is its height in CSS
//                  pixels. The input layer uses them so a click on the bar is
//                  not also a click on the battlefield, and so edge-scrolling
//                  does not trigger while the pointer is over the chrome.

import { UnitDefs, BuildingDefs, ResearchDefs, ResourceDefs } from '../sim/defs.js';
import { MAX_SUPPLY, PLAYER, SELECTION_LIMIT } from '../sim/constants.js';
import {
  commandCard, findByHotkey, costLabel, targetsOf, isWorker,
  CARD_SLOTS, QUEUE_LIMIT,
} from './commandcard.js';

const FLASH_MS = 2600;
const ALERT_GRACE_TICKS = 2;     // see the onAlert hook
const TILE_LIMIT = SELECTION_LIMIT;  // one tile per selectable unit, no more

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export const mmss = (t) => {
  const s = Math.max(0, Math.floor(t));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Weapon a unit is currently using. Mirrors combat.weaponOf without importing it. */
export function weaponFor(e) {
  if (!e) return null;
  if (e.kind === 'building') return (BuildingDefs[e.defId] && BuildingDefs[e.defId].weapon) || null;
  const d = UnitDefs[e.defId];
  if (!d) return null;
  return e.sieged && d.siegeWeapon ? d.siegeWeapon : d.weapon || null;
}

export function displayName(e) {
  if (!e) return '';
  if (e.kind === 'unit') return (UnitDefs[e.defId] || {}).name || e.defId;
  if (e.kind === 'building') return (BuildingDefs[e.defId] || {}).name || e.defId;
  return (ResourceDefs[e.defId] || {}).name || e.defId;
}

export function shortName(e) {
  if (!e) return '';
  if (e.kind === 'unit') return (UnitDefs[e.defId] || {}).short || e.defId.slice(0, 3).toUpperCase();
  if (e.kind === 'building') return (BuildingDefs[e.defId] || {}).short || e.defId.slice(0, 3).toUpperCase();
  return e.defId === 'geyser' ? 'GAS' : 'MIN';
}

/**
 * A worker with nothing to do.
 *
 * economy.js puts a worker that still holds cargo or still owns a patch straight
 * back to work, so "no order" alone is not enough -- the two states that survive
 * a tick and genuinely need the player's attention are a worker that finished a
 * move/build and was never re-tasked, and one whose gather order cannot find a
 * patch at all. A unit is never called idle on the tick it spawned; it has not
 * had a chance to be given anything yet.
 */
export function isIdleWorker(e, world) {
  if (!e || !e.alive || e.kind !== 'unit') return false;
  const d = UnitDefs[e.defId];
  if (!d || !d.isWorker) return false;
  if (e.orderQueue && e.orderQueue.length) return false;
  if (world && world.tick <= (e.spawnTick || 0)) return false;
  const o = e.order;
  if (!o) return true;
  return o.type === 'gather' && o.targetId == null && e.gatherNode == null && !e.carrying;
}

// --- tiny diffing helpers --------------------------------------------------
// Every write to the DOM in this file goes through one of these. `el._v*` are
// the last values written; comparing them is far cheaper than the layout the
// browser would do on a redundant assignment.

function setText(el, v) {
  const s = v == null ? '' : String(v);
  if (el._vText !== s) { el._vText = s; el.textContent = s; }
}
function setClass(el, cls, on) {
  const k = '_vc_' + cls;
  const want = !!on;
  if (el[k] !== want) { el[k] = want; el.classList.toggle(cls, want); }
}
function setWidthPct(el, pct) {
  const v = `${Math.max(0, Math.min(100, pct)).toFixed(1)}%`;
  if (el._vWidth !== v) { el._vWidth = v; el.style.width = v; }
}
function setAttr(el, k, v) {
  const key = '_va_' + k;
  const gone = v == null || v === false;
  // The cache key must distinguish "absent" from "present but empty", or
  // toggling `disabled` on and off would look like no change at all.
  const s = gone ? '\u0000' : String(v);
  if (el[key] !== s) {
    el[key] = s;
    if (gone) el.removeAttribute(k); else el.setAttribute(k, String(v));
  }
}
function el(tag, cls, parent) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (parent) parent.appendChild(n);
  return n;
}

// ---------------------------------------------------------------------------

export class HUD {
  constructor({ root, world, player = PLAYER, input = null, renderer = null, minimap = null } = {}) {
    this.root = root;
    this.world = world;
    this.player = player;
    this.input = input;
    this.renderer = renderer;
    this.minimap = minimap;

    /** 'root' | 'build' -- which page of the command card is showing. */
    this.mode = 'root';
    this.card = { mode: 'root', slots: new Array(CARD_SLOTS).fill(null) };

    /**
     * True only when nothing else is handling the keyboard.
     *
     * Backing field for the getter below. Whether the HUD listens is NOT a
     * convention two modules agree to observe -- while an input controller is
     * attached this reads false no matter what anyone assigns, because that
     * controller resolves keys against this same command card and running both
     * would queue two units for one press.
     */
    this._ownsHotkeys = true;

    this._selOrder = [];
    this._selEnts = [];
    this._idle = [];
    this._idleCursor = 0;
    this._flash = null;
    this._alertAt = -Infinity;
    this._lastEvents = null;
    this._lastKey = '';
    this._lastRun = '';
    this._booting = true;
    this._prev = { minerals: -1, gas: -1, supplyUsed: -1, supplyMax: -1, clock: '', idle: -1 };
    this._slotSig = new Array(CARD_SLOTS).fill(null);
    this._tiles = new Map();
    this._barH = 0;

    // Note there is deliberately nothing here about `_ownsHotkeys`: ownership
    // is DERIVED from `this.input` by the getter below, so attaching an input
    // controller later (which is what main.js does) settles it either way.

    this._build();
    this.layout();
    this._onResize = () => this.layout();
    if (typeof addEventListener === 'function') {
      addEventListener('resize', this._onResize);
      addEventListener('keydown', (e) => this._onKeyDown(e));
    }
    this.update();
  }

  // -- construction ---------------------------------------------------------
  _build() {
    const root = this.root;
    root.classList.add('hud-root');
    root.replaceChildren();

    // top strip: resources, supply, clock
    const top = el('div', 'hud-top', root);
    this.elMinerals = this._res(top, 'min', 'Minerals');
    this.elGas = this._res(top, 'gas', 'Vespene Gas');
    this.elSupply = this._res(top, 'sup', 'Supply');
    this.elClock = this._res(top, 'clock', 'Elapsed');
    this.elSupplyBox = this.elSupply.parentNode;

    // the supply-blocked shout. A player who does not notice being supply
    // blocked blames the game, not themselves -- so it is loud, centred, and
    // sits directly under the number it is talking about.
    this.elWarn = el('div', 'hud-warn', root);
    setText(this.elWarn, 'Additional supply required');

    this.elFlash = el('div', 'hud-flash', root);
    this.elBanner = el('div', 'hud-banner', root);

    // While a building ghost is armed, the reason it cannot go down HERE. The
    // ghost itself paints per-tile red/green, which tells a player everything
    // and tells a log nothing; validatePlacement() already returns the
    // sentence, so this puts it where both can read it.
    this.elPlace = el('div', 'hud-place', root);

    const bar = el('div', 'hud-bar', root);
    this.elBar = bar;

    // --- minimap ---------------------------------------------------------
    const mmWrap = el('div', 'hud-minimap', bar);
    this.minimapCanvas = document.createElement('canvas');
    this.minimapCanvas.className = 'minimap-canvas';
    mmWrap.appendChild(this.minimapCanvas);

    // --- selection panel -------------------------------------------------
    const panel = el('div', 'hud-panel', bar);
    this.elPanel = panel;

    const hint = el('div', 'sel-empty', panel);
    this.elHint = hint;
    setText(el('div', 'sel-hint-line', hint), 'Nothing selected');
    setText(el('div', 'sel-hint-sub', hint), 'Left-click a unit, or drag a box. Right-click to order.');

    const one = el('div', 'sel-one', panel);
    this.elOne = one;
    const port = el('div', 'sel-portrait', one);
    this.elPortrait = port;
    this.elPortraitCode = el('span', 'p-code', port);
    const info = el('div', 'sel-info', one);
    this.elName = el('div', 'sel-name', info);
    const hp = el('div', 'sel-hp', info);
    this.elHpBar = el('div', 'bar', hp);
    this.elHpFill = el('div', 'fill', this.elHpBar);
    this.elHpText = el('div', 'txt', hp);
    this.elStats = el('div', 'sel-stats', info);
    this.elStatCells = [];
    for (let i = 0; i < 4; i++) {
      const cell = el('div', 'stat', this.elStats);
      const k = el('span', 'k', cell);
      const v = el('span', 'v', cell);
      this.elStatCells.push({ cell, k, v });
    }
    this.elDesc = el('div', 'sel-desc', info);

    // production queue (buildings)
    const q = el('div', 'sel-queue', one);
    this.elQueue = q;
    this.elQueueSlots = [];
    for (let i = 0; i < QUEUE_LIMIT; i++) {
      const s = el('div', 'q-slot', q);
      const code = el('span', 'q-code', s);
      const bar2 = el('div', 'q-bar', s);
      const fill = el('div', 'q-fill', bar2);
      s.addEventListener('mousedown', (ev) => { this._stop(ev); this._cancelQueueSlot(i); });
      setAttr(s, 'title', 'Click to cancel');
      this.elQueueSlots.push({ s, code, fill });
    }

    const many = el('div', 'sel-many', panel);
    this.elMany = many;

    // The selection cap is 24 and a late-game box drag covers 70. A silent cap
    // is how "select army, attack" leaves two thirds of the army at home, so
    // the shortfall gets a standing line here as well as a flash at the time.
    // Its text is cleared when it does not apply: an element that is invisible
    // by CSS alone still reads as on-screen to anything scraping the HUD.
    this.elCap = el('div', 'sel-cap', panel);

    // A production key the player pressed and did not get, with the reason,
    // for as long as the reason holds. The centre-screen flash says it once and
    // fades in 2.6 s; a player macroing off the card is looking down here and
    // misses it, which is how "my army stopped growing" happens with 1500
    // minerals in the bank. input.js maintains the state (productionStatus);
    // this is only where it is written down. Same text cleared when it stops
    // applying -- an element hidden by CSS alone still reads as on-screen.
    this.elHold = el('div', 'sel-hold', panel);

    // --- command card ----------------------------------------------------
    const card = el('div', 'hud-card', bar);
    this.elCard = card;
    this.elSlots = [];
    for (let i = 0; i < CARD_SLOTS; i++) {
      const slot = el('div', 'cmd-slot empty', card);
      const btn = el('div', 'cmd-btn', slot);
      setAttr(btn, 'role', 'button');
      const key = el('span', 'cmd-key', btn);
      const name = el('span', 'cmd-name', btn);
      const cost = el('span', 'cmd-cost', btn);
      const cm = el('span', 'm', cost);
      const cg = el('span', 'g', cost);
      btn.addEventListener('mousedown', (ev) => { this._stop(ev); this._clickSlot(i); });
      btn.addEventListener('mouseenter', () => this._showTip(i));
      btn.addEventListener('mouseleave', () => this._hideTip());
      this.elSlots.push({ slot, btn, key, name, cost, cm, cg });
    }

    this.elTip = el('div', 'hud-tip', bar);
    this.elTipName = el('div', 'tip-name', this.elTip);
    this.elTipCost = el('div', 'tip-cost', this.elTip);
    this.elTipDesc = el('div', 'tip-desc', this.elTip);
    this.elTipWhy = el('div', 'tip-why', this.elTip);

    // --- idle worker chip -------------------------------------------------
    const idle = el('div', 'hud-idle', bar);
    this.elIdle = idle;
    this.elIdleCount = el('span', 'n', idle);
    const lbl = el('span', 'l', idle);
    setText(lbl, 'idle');
    idle.addEventListener('mousedown', (ev) => { this._stop(ev); this.selectNextIdleWorker(); });
    setAttr(idle, 'title', 'Select the next idle worker');

    // Swallow the pointer events that would otherwise be read as commands on
    // the battlefield behind the chrome.
    //
    // NOT mouseup and NOT mousemove: input.js tracks those on the window so a
    // drag that ends over the bar still finishes. Eating them here would leave
    // a selection box stuck on screen -- the exact bug its comment warns about.
    for (const node of [top, bar]) {
      for (const t of ['contextmenu', 'dblclick', 'wheel']) {
        node.addEventListener(t, (ev) => this._stop(ev));
      }
    }
  }

  _res(parent, kind, title) {
    const box = el('div', `res res-${kind}`, parent);
    setAttr(box, 'title', title);
    el('span', 'ico', box);
    return el('span', 'val', box);
  }

  _stop(ev) {
    if (!ev) return;
    if (typeof ev.stopPropagation === 'function') ev.stopPropagation();
    if (typeof ev.preventDefault === 'function' && ev.type !== 'mousedown') ev.preventDefault();
  }

  // -- layout ---------------------------------------------------------------
  /** Height of the command bar in CSS pixels. Mirrors --bar-h in hud.css. */
  get barHeight() { return this._barH; }

  /**
   * The bar is capped at 20% of the viewport (the brief allows 22) and never
   * shrinks below the point where the minimap stops being readable.
   */
  layout() {
    const h = (typeof window !== 'undefined' && window.innerHeight) || 900;
    const barH = Math.round(Math.max(148, Math.min(240, h * 0.20)));
    this._barH = barH;
    if (this.root && this.root.style) this.root.style.setProperty('--bar-h', `${barH}px`);

    // The minimap is a square inset in the bar. The HUD owns its CSS box AND
    // its device-pixel backing store; the Minimap module only draws.
    const side = Math.max(96, barH - 16);
    const c = this.minimapCanvas;
    if (c) {
      c.style.width = `${side}px`;
      c.style.height = `${side}px`;
      const dpr = Math.min(3, Math.max(1, (typeof globalThis !== 'undefined' && globalThis.devicePixelRatio) || 1));
      const px = Math.max(1, Math.round(side * dpr));
      if (c.width !== px || c.height !== px) { c.width = px; c.height = px; }
    }
    if (this.minimap && typeof this.minimap.resize === 'function') this.minimap.resize();
  }

  /** True for screen points covered by HUD chrome. Used by the input layer. */
  isOverHud(x, y) {
    const h = (typeof window !== 'undefined' && window.innerHeight) || 900;
    const w = (typeof window !== 'undefined' && window.innerWidth) || 1600;
    if (y >= h - this._barH) return true;
    if (y <= 40 && x >= w - 420) return true;
    return false;
  }

  // -- events ---------------------------------------------------------------
  /**
   * ONE module drains world.drainEvents(); that module is the renderer. The HUD
   * reads the batch back off it, and only drains itself when there is no
   * renderer at all (which is how test/hud.test.js runs it).
   */
  _renderer() {
    if (this.renderer) return this.renderer;
    // globalThis.GAME is only trusted when it is describing THIS hud -- a test
    // process that booted the real build earlier must not leak its renderer
    // into a stand-alone HUD built over a different world.
    const g = typeof globalThis !== 'undefined' ? globalThis.GAME : null;
    const r = (this.input && this.input.renderer) || (g && g.hud === this && g.renderer) || null;
    if (r) this.renderer = r;
    return r;
  }

  _events() {
    const r = this._renderer();
    if (r) return typeof r.getFrameEvents === 'function' ? r.getFrameEvents() : null;
    return this.world.drainEvents();
  }

  _pumpEvents() {
    // Never drain during construction: in the real build the renderer exists but
    // has not been wired to this HUD yet, and stealing the opening spawn batch
    // from it would be a silent, permanent bug.
    if (this._booting) { this._booting = false; return; }
    const evs = this._events();
    if (!evs || evs === this._lastEvents) return;
    this._lastEvents = evs;
    for (let i = 0; i < evs.length; i++) {
      const ev = evs[i];
      if (ev.type === 'error') {
        if (ev.player === this.player) this.flash(ev.msg);
      } else if (ev.type === 'researched' && ev.owner === this.player) {
        const r = ResearchDefs[ev.defId];
        this.flash(`${r ? r.name : ev.defId} complete`);
      }
    }
  }

  // -- keyboard ownership ---------------------------------------------------
  /**
   * ENFORCED, not agreed. An attached input controller owns the keyboard; this
   * getter reports false for as long as one is attached, and the setter can
   * only change what happens in the degenerate no-input-layer case. That is
   * what makes it impossible for both listeners to claim the same key -- the
   * failure that made the build menu keyboard-dead.
   */
  get ownsHotkeys() {
    if (this.input && typeof this.input.runAction === 'function') return false;
    return this._ownsHotkeys;
  }
  set ownsHotkeys(v) { this._ownsHotkeys = !!v; }

  // -- public API -----------------------------------------------------------
  /** Transient centre-screen text for a refusal the simulation just emitted. */
  flash(message) {
    if (!message) return;
    const t = now();
    if (this._flash && this._flash.msg === message && t - this._flash.t < 500) {
      this._flash.t = t;                    // same refusal twice: refresh, do not stack
      return;
    }
    this._flash = { msg: message, t };
    // Paint it now rather than waiting for the next update(): a refusal that
    // appears one frame late is a refusal the player already stopped looking for.
    if (this.elFlash) { setText(this.elFlash, message); setClass(this.elFlash, 'show', true); }
  }

  /**
   * Which page of the card is showing.
   *
   * The build submenu is MODAL STATE, and input.js already owns modal state --
   * it has to, because 'B' then 'S' has to mean Supply Depot to the keyboard.
   * So the HUD reflects input.mode rather than keeping a second copy that can
   * disagree with it. Without an input controller it falls back to its own.
   */
  get cardMode() {
    const input = this.input;
    if (!input || typeof input.mode !== 'string') return this.mode;
    // input.js exports the same derivation (cardPageFor) that its key resolver
    // uses, so the card the player is looking at and the card the keyboard is
    // resolving against are literally the same object shape from the same rule.
    if (typeof input.cardMode === 'string') return input.cardMode;
    // A ghost that is already armed keeps the submenu up, exactly like BW: the
    // structure keys stay live, so B S S lays two depots in a row.
    return input.mode === 'build' || input.mode === 'placement' ? 'build' : 'root';
  }

  setMode(mode) {
    if (mode === 'build') { if (this._inputAct({ type: 'buildmenu' })) { this._hideTip(); return; } }
    else if (this.cardMode !== 'root') {
      const input = this.input;
      if (input && typeof input.cancelMode === 'function') { input.cancelMode(); this._hideTip(); return; }
      if (this._inputAct({ type: 'escape' })) { this._hideTip(); return; }
    }
    if (this.mode === mode) return;
    this.mode = mode;
    this._hideTip();
  }

  /**
   * The command-card button a key maps to, or null.
   *
   * Kept for callers that want to ask the card a question. It is NOT the
   * keyboard path any more: input.js resolves keys against a card it builds
   * itself, precisely because `this.card` is only as fresh as the last
   * update() -- answering the second key of "B, S" from the card that was on
   * screen before B is what made the build menu keyboard-dead.
   */
  actionForKey(key) {
    this.ownsHotkeys = false;
    if (key === 'Escape' && this.cardMode !== 'root') return { id: 'cancelmenu', enabled: true, action: { type: 'cancelmenu', targeted: false }, hotkey: 'Escape' };
    return findByHotkey(this.card, key);
  }

  /** Execute a command-card button. `source` is 'click' or 'key'. */
  runAction(btn, source = 'click') {
    if (!btn) return false;
    const a = btn.action || btn;
    if (!a || !a.type) return false;

    // The headless player dispatches every key on both window and document, and
    // a browser will happily deliver a keydown to two listeners. Identical
    // actions at the identical timestamp are the same press.
    if (source === 'key') {
      const stamp = `${a.type}:${a.defId || ''}@${now()}`;
      if (stamp === this._lastRun) return false;
      this._lastRun = stamp;
    }

    if (btn.enabled === false) { this.flash(btn.reason || 'Unavailable'); return false; }

    const ents = targetsOf(a, this._selEnts, this.player);
    const ids = ents.map((e) => e.id);
    const input = this.input;

    switch (a.type) {
      case 'buildmenu':
        if (!ids.length) return false;
        this.setMode('build');
        return true;

      case 'cancelmenu':
        this.setMode('root');
        return true;

      case 'build':
        if (input && typeof input.beginPlacement === 'function') return !!input.beginPlacement(a.defId);
        if (input && typeof input.startPlacement === 'function') return !!input.startPlacement(a.defId);
        if (this._inputAct({ type: 'placement', defId: a.defId })) return true;
        return this._targetHint(a);

      // Move / Attack / Patrol arm the input controller so the next left click on
      // the battlefield becomes the order. beginTargeting() carries the exact
      // ids the button addresses; the cursor action is the older, selection-wide
      // path and is kept as a fallback. Either way this is the same state the
      // M / A / P keys set, which is what makes a button and its key one
      // behaviour rather than two implementations of one idea.
      case 'move': case 'attack': case 'patrol': {
        if (!ids.length) return false;
        if (this._beginTargeting(a, ids)) return true;
        const cursor = a.type === 'attack' ? 'attackmove' : a.type;
        if (this._inputAct({ type: 'cursor', cursor })) return true;
        return this._targetHint(a);
      }

      case 'stop': case 'hold':
        if (!ids.length) return false;
        if (this._inputAct({ type: a.type })) return true;
        this._issue({ type: a.type, player: this.player, ids });
        return true;

      case 'siege': case 'unsiege':
        if (!ids.length) return false;
        if (this._inputAct({ type: 'siege' })) return true;
        this._issue({ type: a.type, player: this.player, ids });
        return true;

      case 'cloak': case 'decloak':
        if (!ids.length) return false;
        if (this._inputAct({ type: 'cloak' })) return true;
        this._issue({ type: a.type, player: this.player, ids });
        return true;

      case 'train':
        if (!ids.length) return false;
        if (input && typeof input.issueTrain === 'function') return !!input.issueTrain(a.defId);
        if (this._inputAct({ type: 'train', defId: a.defId })) return true;
        this._issue({ type: 'train', player: this.player, ids, defId: a.defId });
        return true;

      case 'research':
        if (!ids.length) return false;
        if (input && typeof input.issueResearch === 'function') return !!input.issueResearch(a.defId);
        if (this._inputAct({ type: 'research', defId: a.defId })) return true;
        this._issue({ type: 'research', player: this.player, ids, defId: a.defId });
        return true;

      case 'cancel':
        if (!ids.length) return false;
        if (input && typeof input.cancelProduction === 'function') return !!input.cancelProduction(a.slot);
        this._issue({ type: 'cancel', player: this.player, ids, slot: a.slot });
        return true;

      // "Back to work". Right-clicking a patch picks a specific one; the button
      // is the bulk version, which is what you actually want after a build.
      case 'gather':
        if (!ids.length) return false;
        if (this._beginTargeting(a, ids)) return true;
        this._issue({ type: 'gather', player: this.player, ids, targetId: null });
        return true;

      case 'repair': case 'rally':
        if (!ids.length) return false;
        if (this._beginTargeting(a, ids)) return true;
        return this._targetHint(a);

      default:
        return false;
    }
  }

  /** Push an action into input.js's own action vocabulary. */
  _inputAct(action) {
    const input = this.input;
    if (!input || typeof input.runAction !== 'function') return false;
    input.runAction(action, {});
    return true;
  }

  /**
   * "The next click is the target." input.js implements beginTargeting(req) and
   * arms its cursor with the ids this button addresses; the aliases below are
   * there so a differently-named implementation still works.
   */
  _beginTargeting(a, ids) {
    const input = this.input;
    if (!input) return false;
    const req = { type: a.type, player: this.player, ids, defId: a.defId || null };
    for (const m of ['beginTargeting', 'startTargeting', 'setPending', 'beginCommand']) {
      if (typeof input[m] === 'function') { input[m](req); return true; }
    }
    return false;
  }

  /** Tell the player which mouse gesture does the thing they just clicked. */
  _targetHint(a) {
    const hint = {
      move: 'Right-click the ground to move',
      attack: 'Right-click a target to attack',
      patrol: 'Right-click to set the patrol point',
      gather: 'Right-click a mineral field to gather',
      repair: 'Right-click a damaged structure to repair',
      rally: 'Right-click the map to set the rally point',
      build: 'Click the map to place the structure',
    }[a.type];
    if (hint) this.flash(hint);
    return false;
  }

  /** Select the next idle worker and put the camera on it, like F1 in SC2. */
  selectNextIdleWorker() {
    const list = this._idle;
    if (!list.length) return false;
    this._idleCursor = (this._idleCursor + 1) % list.length;
    const id = list[this._idleCursor];
    const e = this.world.get(id);
    if (!e) return false;
    this._applySelection([id]);
    this.setMode('root');
    const cam = this._camera();
    if (cam && typeof cam.centerOn === 'function') cam.centerOn(e.x, e.y);
    return true;
  }

  // -- command routing ------------------------------------------------------
  _camera() {
    const r = this.renderer || (this.input && this.input.renderer) || null;
    return r ? r.camera : null;
  }

  _issue(cmd) {
    const input = this.input;
    if (input && typeof input.issue === 'function') { input.issue(cmd); return; }
    this.world.issueNow(cmd);
  }

  /**
   * The HUD's keyboard listener, which in the shipped game NEVER RUNS.
   *
   * There is no longer a split: input.js resolves every key against this same
   * command card, so a second table here could only ever disagree with it.
   * This exists for a HUD mounted with no input layer at all -- the way parts
   * of test/hud.test.js build it -- and `ownsHotkeys` (a getter) is what
   * enforces that, rather than a rule about which keys each side is allowed
   * to claim.
   */
  _onKeyDown(ev) {
    if (!ev) return;
    if (!this.ownsHotkeys) return;
    const key = ev.key;
    if (!key) return;
    // One press can arrive twice: test/browser/player.js dispatches on window
    // AND document, and the shim re-delivers document events to window.
    const stamp = `${key}@${now()}`;
    if (stamp === this._lastKey) return;
    this._lastKey = stamp;

    if (key === 'Escape') {
      if (this.cardMode !== 'root') {
        this.setMode('root');
        if (ev.preventDefault) ev.preventDefault();
      }
      return;
    }
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return;   // control groups are the input layer's
    const btn = findByHotkey(this.card, key);
    if (!btn) return;
    if (ev.preventDefault) ev.preventDefault();
    this.runAction(btn, 'key');
  }

  _clickSlot(i) {
    const btn = this.card.slots[i];
    if (!btn) return;
    this.runAction(btn, 'click');
  }

  _cancelQueueSlot(i) {
    const e = this._selEnts.length === 1 ? this._selEnts[0] : null;
    if (!e || e.kind !== 'building' || e.owner !== this.player) return;
    if (!e.queue || i >= e.queue.length) return;
    const input = this.input;
    if (input && typeof input.cancelProduction === 'function') { input.cancelProduction(i); return; }
    this._issue({ type: 'cancel', player: this.player, ids: [e.id], slot: i });
  }

  _applySelection(ids) {
    const input = this.input;
    if (!input) return false;
    if (typeof input.setSelection === 'function') { input.setSelection(ids); return true; }
    if (typeof input.select === 'function') { input.select(ids); return true; }
    const sel = input.selection;
    if (sel && typeof sel.clear === 'function' && typeof sel.add === 'function') {
      sel.clear();
      for (const id of ids) sel.add(id);
      if (typeof input.onSelectionChanged === 'function') input.onSelectionChanged();
      else if (input.renderer && typeof input.renderer.setSelection === 'function') input.renderer.setSelection([...sel]);
      return true;
    }
    return false;
  }

  _clickTile(id, ev) {
    const remove = !!(ev && (ev.ctrlKey || ev.metaKey || ev.shiftKey));
    const ids = this._selOrder.slice();
    if (remove) {
      const next = ids.filter((x) => x !== id);
      if (next.length) this._applySelection(next);
    } else {
      this._applySelection([id]);
    }
    this.setMode('root');
  }

  // -- tooltip --------------------------------------------------------------
  _showTip(i) {
    const btn = this.card.slots[i];
    if (!btn) { this._hideTip(); return; }
    setText(this.elTipName, btn.hotkey ? `${btn.label}  [${btn.hotkey}]` : btn.label);
    setText(this.elTipCost, btn.cost ? `${btn.cost.minerals || 0} minerals${btn.cost.gas ? `   ${btn.cost.gas} gas` : ''}` : '');
    setText(this.elTipDesc, btn.desc || '');
    setText(this.elTipWhy, btn.reason || '');
    setClass(this.elTip, 'show', true);
    setClass(this.elTip, 'bad', !btn.enabled);
  }
  _hideTip() { setClass(this.elTip, 'show', false); }

  // -- frame ----------------------------------------------------------------
  update() {
    const w = this.world;
    const pl = w.players[this.player];
    // Keyboard ownership needs no per-frame maintenance any more: `ownsHotkeys`
    // is a getter that reads the attached input controller directly.
    if (!this.minimap && this.input && this.input.minimap) this._attachMinimap(this.input.minimap);

    this._pumpEvents();
    this._updateResources(pl);
    this._updateIdle();

    const sel = this._stableSelection();
    this._selEnts = sel;
    this._updateSelection(sel);
    this._updateCard(sel);
    this._updateOverlays();
  }

  _attachMinimap(mm) {
    this.minimap = mm;
    if (mm && mm.onAlert === null) {
      mm.onAlert = (a) => {
        // Guard the opening tick. combat.js seeds lastDamagedTick to -1 and
        // fog.js's alert window is `tick - FOG_INTERVAL`, so the fogSystem()
        // call inside the World constructor sees -1 >= -2 and reports every
        // unit on the map as under attack. Nothing can be attacked on tick 0
        // -- the two bases are 90 tiles apart -- so drop those. (Reported in
        // contractNotes; this guard is cheap and independent of the fix.)
        if (!a || a.tick <= ALERT_GRACE_TICKS) return;
        const t = now();
        // -Infinity, not 0: performance.now() starts near zero, so a `0` seed
        // would swallow every alert in the opening seconds of the match.
        if (t - this._alertAt < 5000) return;
        this._alertAt = t;
        this.flash('Your forces are under attack');
      };
    }
  }

  _updateResources(pl) {
    const p = this._prev;
    const min = Math.floor(pl.minerals);
    const gas = Math.floor(pl.gas);
    if (min !== p.minerals) { p.minerals = min; setText(this.elMinerals, min); }
    if (gas !== p.gas) { p.gas = gas; setText(this.elGas, gas); }

    const used = pl.supplyUsed, max = Math.min(MAX_SUPPLY, pl.supplyMax);
    if (used !== p.supplyUsed || max !== p.supplyMax) {
      p.supplyUsed = used; p.supplyMax = max;
      setText(this.elSupply, `${used}/${max}`);
    }
    const blocked = used >= max && max < MAX_SUPPLY;
    setClass(this.elSupplyBox, 'blocked', blocked);
    setClass(this.elSupplyBox, 'tight', !blocked && max - used <= 2 && max < MAX_SUPPLY);
    setClass(this.elWarn, 'show', blocked);

    const clk = mmss(this.world.time);
    if (clk !== p.clock) { p.clock = clk; setText(this.elClock, clk); }
  }

  _updateIdle() {
    const list = this._idle;
    list.length = 0;
    const w = this.world;
    for (const id of w.byPlayer[this.player]) {
      const e = w.entities.get(id);
      if (isIdleWorker(e, w)) list.push(id);
    }
    if (this._idleCursor >= list.length) this._idleCursor = 0;
    if (list.length !== this._prev.idle) {
      this._prev.idle = list.length;
      setText(this.elIdleCount, list.length);
      setClass(this.elIdle, 'show', list.length > 0);
    }
  }

  /**
   * The selection order must be STABLE. Tiles that jump around between frames
   * make it impossible to click the one you are looking at, so ids keep the
   * position they were first given and new ids are appended.
   */
  _stableSelection() {
    const input = this.input;
    // input.js publishes selectionOrder specifically for this panel; its Set is
    // built from that list, so either works, but the array is the stated contract.
    const raw = input
      ? (Array.isArray(input.selectionOrder) ? input.selectionOrder : input.selection ? [...input.selection] : [])
      : [];
    const live = new Set();
    for (const id of raw) { const e = this.world.get(id); if (e && e.alive) live.add(id); }

    const order = [];
    for (const id of this._selOrder) if (live.has(id)) order.push(id);
    const seen = new Set(order);
    for (const id of raw) if (live.has(id) && !seen.has(id)) { order.push(id); seen.add(id); }
    this._selOrder = order;

    const ents = [];
    for (const id of order) ents.push(this.world.get(id));
    return ents;
  }

  _updateSelection(sel) {
    const n = sel.length;
    setClass(this.elHint, 'show', n === 0);
    setClass(this.elOne, 'show', n === 1);
    setClass(this.elMany, 'show', n > 1);
    if (n === 1) this._updateOne(sel[0]);
    if (n > 1) this._updateMany(sel);
    this._updateCapNotice(n);
  }

  /** "the box covered 70, you have 24" -- for as long as those 24 are selected. */
  _updateCapNotice(n) {
    const left = (this.input && this.input.selectionCapped) || 0;
    const show = left > 0 && n >= SELECTION_LIMIT;
    setClass(this.elCap, 'show', show);
    setText(this.elCap, show
      ? `Selection full: ${n} of ${n + left} — ${left} not selected (limit ${SELECTION_LIMIT})`
      : '');
  }

  _updateOne(e) {
    const mine = e.owner === this.player;
    setText(this.elName, displayName(e));
    setClass(this.elOne, 'enemy', e.owner >= 0 && !mine);
    setClass(this.elPortrait, 'enemy', e.owner >= 0 && !mine);
    setClass(this.elPortrait, 'neutral', e.owner < 0);
    setText(this.elPortraitCode, shortName(e));

    if (e.kind === 'resource') {
      setClass(this.elHpBar, 'hide', true);
      setText(this.elHpText, `${Math.max(0, Math.round(e.amount))} remaining`);
    } else {
      setClass(this.elHpBar, 'hide', false);
      const frac = e.maxHp ? e.hp / e.maxHp : 0;
      setWidthPct(this.elHpFill, frac * 100);
      setClass(this.elHpFill, 'warn', frac <= 0.6 && frac > 0.3);
      setClass(this.elHpFill, 'bad', frac <= 0.3);
      setText(this.elHpText, `${Math.max(0, Math.ceil(e.hp))} / ${e.maxHp}`);
    }

    // stat cells: armour / damage / range / status
    const w = weaponFor(e);
    const cells = this.elStatCells;
    const put = (i, k, v) => {
      const c = cells[i];
      setClass(c.cell, 'hide', v == null);
      if (v == null) return;
      setText(c.k, k); setText(c.v, v);
    };
    if (e.kind === 'resource') {
      put(0, null, null); put(1, null, null); put(2, null, null); put(3, null, null);
    } else {
      put(0, 'ARM', String(e.armor || 0));
      put(1, 'DMG', w ? `${w.damage} ${w.type[0].toUpperCase()}` : '--');
      put(2, 'RNG', w ? `${w.range}` : '--');
      const status = e.constructing ? `${Math.round((e.buildProgress || 0) * 100)}%`
        : e.sieged ? 'SIEGED'
        : e.cloaked ? 'CLOAKED'
        : e.kind === 'unit' && e.carrying ? `+${e.carrying.amount} ${e.carrying.type === 'gas' ? 'gas' : 'min'}`
        : null;
      put(3, e.constructing ? 'BLD' : 'STA', status);
    }

    const def = e.kind === 'unit' ? UnitDefs[e.defId] : e.kind === 'building' ? BuildingDefs[e.defId] : null;
    setText(this.elDesc, def && def.desc ? def.desc : '');

    // production queue
    const q = (e.kind === 'building' && mine && e.queue) ? e.queue : null;
    setClass(this.elQueue, 'show', !!(q && q.length));
    if (q) {
      for (let i = 0; i < this.elQueueSlots.length; i++) {
        const s = this.elQueueSlots[i];
        const item = q[i];
        setClass(s.s, 'hide', !item);
        if (!item) continue;
        const d = item.kind === 'unit' ? UnitDefs[item.defId] : ResearchDefs[item.defId];
        setText(s.code, d ? (d.short || d.name.slice(0, 3).toUpperCase()) : item.defId);
        const done = item.total ? 1 - item.timeLeft / item.total : 0;
        setWidthPct(s.fill, done * 100);
        setClass(s.s, 'research', item.kind === 'research');
      }
    }
  }

  _updateMany(sel) {
    const wanted = sel.slice(0, TILE_LIMIT);
    const seen = new Set();
    for (let i = 0; i < wanted.length; i++) {
      const e = wanted[i];
      seen.add(e.id);
      let t = this._tiles.get(e.id);
      if (!t) {
        const node = el('div', 'sel-tile');
        setAttr(node, 'data-id', e.id);
        node.dataset.id = String(e.id);
        const code = el('span', 'code', node);
        const bar = el('div', 'tbar', node);
        const fill = el('div', 'tfill', bar);
        node.addEventListener('mousedown', (ev) => { this._stop(ev); this._clickTile(e.id, ev); });
        t = { node, code, fill, index: -1 };
        this._tiles.set(e.id, t);
      }
      // Only touch the DOM position when the index actually moved. Selection
      // order is stable, so in practice this runs once per selection change.
      if (t.index !== i) {
        t.index = i;
        const at = this.elMany.childNodes[i];
        if (at !== t.node) this.elMany.insertBefore(t.node, at || null);
      }
      setText(t.code, shortName(e));
      const frac = e.maxHp ? e.hp / e.maxHp : 1;
      setWidthPct(t.fill, frac * 100);
      setClass(t.fill, 'warn', frac <= 0.6 && frac > 0.3);
      setClass(t.fill, 'bad', frac <= 0.3);
      setClass(t.node, 'enemy', e.owner >= 0 && e.owner !== this.player);
    }
    for (const [id, t] of this._tiles) {
      if (seen.has(id)) continue;
      t.node.remove();
      this._tiles.delete(id);
    }
  }

  _updateCard(sel) {
    if (this.mode === 'build' && !sel.some((e) => isWorker(e) && e.owner === this.player)) this.mode = 'root';
    const card = commandCard(this.world, this.player, sel, this.cardMode);
    this.card = card;
    for (let i = 0; i < CARD_SLOTS; i++) {
      const b = card.slots[i];
      const sig = b ? `${b.id}|${b.label}|${b.hotkey}|${b.cost ? costLabel(b.cost) : ''}|${b.enabled ? 1 : 0}|${b.reason || ''}` : '';
      if (this._slotSig[i] === sig) continue;
      this._slotSig[i] = sig;
      const s = this.elSlots[i];
      setClass(s.slot, 'empty', !b);
      if (!b) { setAttr(s.btn, 'data-hotkey', null); setText(s.name, ''); setText(s.key, ''); setText(s.cm, ''); setText(s.cg, ''); continue; }
      setText(s.key, b.hotkey === 'Escape' ? 'ESC' : b.hotkey);
      setText(s.name, b.label);
      // Clear the numbers rather than only hiding them: a hidden-but-present
      // "50" left over from the previous card is still in the accessible text
      // and still in anything that reads the button, which is a lie.
      const hasCost = !!b.cost;
      setClass(s.cost, 'hide', !hasCost);
      setText(s.cm, hasCost ? String(b.cost.minerals || 0) : '');
      setText(s.cg, hasCost && b.cost.gas ? String(b.cost.gas) : '');
      setClass(s.cg, 'hide', !(hasCost && b.cost.gas));
      setClass(s.btn, 'disabled', !b.enabled);
      setClass(s.btn, 'menu', b.kind === 'menu');
      setClass(s.btn, 'ability', b.kind === 'ability');
      setAttr(s.btn, 'data-hotkey', b.hotkey || null);
      s.btn.dataset.hotkey = b.hotkey || '';
      setAttr(s.btn, 'disabled', b.enabled ? null : '');
      setAttr(s.btn, 'title', b.enabled ? `${b.label} [${b.hotkey}]` : `${b.label} — ${b.reason}`);
    }
  }

  _hidePlacement() {
    setClass(this.elPlace, 'show', false);
    setClass(this.elPlace, 'bad', false);
    setText(this.elPlace, '');
  }

  /**
   * The placement ghost, in words. `input.placementStatus` carries the exact
   * string src/sim/commands.js would have refused with, so "building placement
   * rejects a lot of visually open ground with no explanation" becomes a
   * sentence anything can read -- a player glancing down, or a test.
   */
  _updatePlacement() {
    const st = this.input && this.input.placementStatus;
    if (!st || !st.defId) { this._hidePlacement(); return; }
    const name = (BuildingDefs[st.defId] || {}).name || st.defId;
    const msg = st.ok ? `${name}: click to place` : `${name}: ${st.reason || 'Cannot build there'}`;
    setClass(this.elPlace, 'show', true);
    setClass(this.elPlace, 'bad', !st.ok);
    setText(this.elPlace, msg);
  }

  /**
   * The standing "you asked for a Rifleman and did not get one" line.
   *
   * Mirrors _updatePlacement: input.js already worked out both the item and the
   * exact sentence the simulation refused with, so this only has to publish it.
   */
  _updateProductionHold() {
    const st = this.input && this.input.productionStatus;
    const show = !!(st && st.defId && st.reason);
    setClass(this.elHold, 'show', show);
    setText(this.elHold, show ? `${st.label || st.defId} on hold: ${st.reason}` : '');
  }

  _updateOverlays() {
    this._updatePlacement();
    this._updateProductionHold();
    const t = now();
    const f = this._flash;
    const show = !!f && t - f.t < FLASH_MS;
    setClass(this.elFlash, 'show', show);
    if (show) setText(this.elFlash, f.msg);

    const go = this.world.gameOver;
    setClass(this.elBanner, 'show', !!go);
    if (go) this._hidePlacement();
    if (go) {
      const won = go.winner === this.player;
      setText(this.elBanner, won ? 'VICTORY' : 'DEFEAT');
      setClass(this.elBanner, 'lose', !won);
    }
  }
}

export default HUD;
